import { graphql, useStaticQuery } from 'gatsby';
import React, { useState } from 'react';
import { AppConfig } from '../../../environment/app-config.model';
import { DatoCmsEoFormSectionComponent } from '../../../schema/datocms';
import { DatalayerEvents } from '../../../schema/enum/datalayer-events';
import { getColumnSpanClass, getColumnStartClass, getTextAlignmentClass, updateDataLayer } from '../../../utilities';
import { BhtpErrorsAndOmissionsForm } from '../../errors-and-omissions-form/errors-and-omissions-form.component';
import HtmlEntityTextComponent from '../../html-entity-text/html-entity-text-component';
import HtmlStringComponent from '../../html-string/html-string-component';
import Icon from '../../icon/icon';
interface ApiEoLead {
    id: string | undefined;
    agencyName: string;
    agencyState: string;
    contactFirstName: string;
    contactLastName: string;
    contactEmail: string;
    contactPhone: string;
    message: string;
    interestedInEORenewal: boolean;
    eoRenewalDate: string;
    campaignName?: string;
}
interface ApiEoLeadRequest {
    token: string;
    lead: ApiEoLead;
}

interface FormEoLead {
    isRenewal: boolean;
    renewalDate: string;
    agencyName: string;
    state: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    message: string;
}

export const StatesStaticQuery = graphql`query StatesStaticQuery {
    allStateJson  {
        nodes {
            stateOrProvinceIso2
            name
        }
    }
    environment: environmentJson {
        bhtpConsumerGatewayBaseUrl
        recaptchaSiteKey
    }
}`;

const EoFormSectionComponent = (
    { columnLayout, title, titleAlignment, descriptionHtml
    }: DatoCmsEoFormSectionComponent
): React.ReactElement => {
    const { allStateJson , environment } = useStaticQuery<StatesStaticQueryResult>(StatesStaticQuery);

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [successful, setSuccessful] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);

    const errorSectionId: string = 'eo-form-error';

    const config = {
        states: allStateJson?.nodes || []
    };

    function setCampaignName(): string {
        let campaignParam: string = '';

        const urlParameters = window.sessionStorage.getItem('urlSearch')?.toString();
        if (urlParameters) {
            const separatedUrlParams: string[] = urlParameters.split('&');
            separatedUrlParams.forEach((urlParam) => {
                if (urlParam.includes('utm_campaign')) {
                    campaignParam = urlParam.split('=')[1];
                }
            });
        }

        return campaignParam;
    }

    function submitted(data: any): void {
        setSubmitting(true);

        const campaignParam: string = setCampaignName();

        const formData: FormEoLead = data.detail as FormEoLead;

        grecaptcha.ready(() => {
            grecaptcha.execute(environment.recaptchaSiteKey || '', { action: 'eo_lead_form'})
                .then((token: string) => {
                    const req: ApiEoLeadRequest = {
                        token,
                        lead: {
                            id: undefined,
                            agencyName: formData.agencyName,
                            agencyState: formData.state,
                            contactFirstName: formData.firstName,
                            contactLastName: formData.lastName,
                            contactEmail: formData.email,
                            contactPhone: formData.phone,
                            message: formData.message,
                            interestedInEORenewal: formData.isRenewal,
                            eoRenewalDate: formData.renewalDate,
                            campaignName: campaignParam !== '' ? campaignParam : undefined
                        }
                    };

                    fetch(`${environment.bhtpConsumerGatewayBaseUrl}eo/lead`, {
                        method: 'POST',
                        cache: 'no-cache',
                        headers: {
                            'content-type': 'application/json'
                        },
                        body: JSON.stringify(req)
                    }).then((resp) => {
                        const success: boolean = resp.ok;
                        setSubmitting(false);
                        if(success) {
                            setSuccessful(success);
                            const customerStatus: string = req.lead.interestedInEORenewal ? 'existing' : 'new';
                            updateDataLayer(DatalayerEvents.EO_submit, {customer_status: customerStatus });
                        } else {
                            setHasError(!success);
                        }
                    }).catch(() => {
                        setSubmitting(false);
                        setHasError(true);
                    });
                });
        });

    }

    const gridColumnSpanClass: string = getColumnSpanClass(columnLayout);
    const gridColumnStartClass: string = getColumnStartClass(columnLayout);

    return (
        <div className="theme-white page-component">
            <div className="container mx-auto lg:grid lg:grid-cols-12">
                <div className={`lg:${gridColumnSpanClass} lg:${gridColumnStartClass}`}>
                    { title && <h2 className={`${getTextAlignmentClass(titleAlignment)} mb-8`}>{ <HtmlEntityTextComponent text={title} /> }</h2> }
                    { descriptionHtml && <HtmlStringComponent className="mb-8" htmlString={descriptionHtml} /> }

                    { hasError && <div id={errorSectionId} className="text-error font-bold mb-8">There was an error and your form could not be submitted. Please try again or contact us at <a href="mailto:agencysales@bhtp.com">agencysales@bhtp.com</a>.</div> }

                    <div className={submitting || successful ? 'hidden' : ''}>
                        <BhtpErrorsAndOmissionsForm config={config} onCallToActionSubmitted={submitted} />
                        <p className="text-brand-gray text-xs text-center pt-4">This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.</p>
                    </div>

                    { submitting && <div className="flex justify-center items-center space-x-2 text-primary"><Icon name="data_saver_off" className="fill-current animate-spin w-8 h-8" /><span>Submitting</span></div> }

                    { successful &&
                        <div className="flex flex-col justify-center text-center mt-16">
                            <h3 className="uppercase">Thank You!</h3>
                            <span>Your information has been submitted, and one of our representatives will be in contact with you shortly.
                                If you have any questions in the meantime, please call <a href="tel:18663753838">866-375-3838</a> or email <a href="mailto:agencysales@bhtp.com">agencysales@bhtp.com</a>.
                            </span>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

interface StatesStaticQueryResult {
    allStateJson : { nodes: { stateOrProvinceIso2: string, name: string }[] };
    environment: Partial<AppConfig>;
}

export default EoFormSectionComponent;
